import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { SyncProblem as SyncIcon } from '@mui/icons-material';

interface OrgChangedDetails {
  previousOrgId: string;
  newOrgId: string;
  newOrgName?: string;
}

/**
 * Blocking dialog shown when the organisation is changed in another tab.
 *
 * Listens to the 'auth:org-changed-in-other-tab' event dispatched by
 * the multiAccountStore's cross-tab storage sync listener.
 *
 * This dialog is non-dismissable - the user must reload to pick up
 * the new organisation context and avoid stale data.
 */
export function OrgChangedDialog() {
  const [open, setOpen] = useState(false);
  const [orgName, setOrgName] = useState<string | undefined>();

  useEffect(() => {
    const handleOrgChanged = (event: Event) => {
      const customEvent = event as CustomEvent<OrgChangedDetails>;
      setOrgName(customEvent.detail.newOrgName);
      setOpen(true);
    };

    window.addEventListener('auth:org-changed-in-other-tab', handleOrgChanged);

    return () => {
      window.removeEventListener(
        'auth:org-changed-in-other-tab',
        handleOrgChanged
      );
    };
  }, []);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="org-changed-dialog-title"
      aria-describedby="org-changed-dialog-description"
    >
      <DialogTitle
        id="org-changed-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <SyncIcon color="warning" />
        Organisation Changed
      </DialogTitle>
      <DialogContent>
        <Typography
          id="org-changed-dialog-description"
          variant="body1"
          color="text.secondary"
          gutterBottom
        >
          {orgName
            ? `The organisation was switched to "${orgName}" in another tab.`
            : 'The organisation was switched in another tab.'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please reload this page to continue with the correct organisation
          context.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleReload}
          variant="contained"
          color="primary"
          autoFocus
        >
          Reload
        </Button>
      </DialogActions>
    </Dialog>
  );
}
